/**
 * 单帧创建页「配色」行(L2)—— V0 §4.3 / 定案 3.2.1、3.2.2。
 *
 * ═══ 单帧此前**完全没有** L2 ═════════════════════════════════════════════════════
 * 长图里用户能定「配色」,到了单帧只剩一个画风 —— 同一个舱两条路径能定的东西不一样,
 * 用户没法解释为什么。补上之后两条路同构。
 *
 * ★这条路径上配色**只作提示词注入**(已核准事实③):单帧海报整张由引擎画、没有 PIL
 * 矢量块,`CreativeOverridesPayload` 也没有 styleTokens 通道 —— 渲成一句色板描述拼进
 * 【风格】段就是正确实现,不为它改 payload schema。
 *
 * ★形制不硬套长图的 `StyleTokensPanel`:那个是**精编**面板(7 个取色器 + 4 个枚举下拉),
 * 给的是「会调色的人」;单帧这一行要的是「选一套就走」。共享的是**规格**(同一批预设、
 * 同一套跟随语义、同一个 `matchStyleTokenPreset` 判据),不是同一个组件
 * (「统一规格不硬套同一组件」)。
 *
 * ★「跟随画风」是**第一个选项**不是一个开关:它与 6 张色卡互斥,同属一个 radiogroup。
 * 做成开关的话,「自由发挥 + 选过配色」就没有退路了(点不回「不指定」)。
 */
import { cn } from "@/shared/lib/utils";
import type { StyleTokens } from "../../../longimage/model/canvas";
import {
  STYLE_TOKEN_PRESETS,
  copyStyleTokens,
  matchStyleTokenPreset,
} from "../../../longimage/model/styleTokenPresets";
import { FONT_STYLE_AUTO, FONT_STYLES } from "./fontStyleOptions";

/**
 * 字形示例用的**近似**字族 —— 只为让用户一眼看出"这一档大概长什么样"。
 * ★不是出图用的字体:图上的字由引擎画,这里只是选择器上的一个视觉提示
 *   (`fontStyleOptions` 头注写得很清楚,那一档本身也只是软提示)。
 */
const FONT_PREVIEW_FAMILY: Record<string, string> = {
  rounded: '"PingFang SC", "Hiragino Sans GB", system-ui, sans-serif',
  handwritten: '"Xingkai SC", "Kaiti SC", "STKaiti", cursive',
  serif: '"Songti SC", "STSong", Georgia, serif',
  sans: '"Helvetica Neue", Helvetica, Arial, sans-serif',
};

interface Props {
  /** 当前配色;null = 不指定,交给引擎自判。 */
  styleTokens: StyleTokens | null;
  /** 选一份配色(hook 侧会顺手熄灭跟随)。 */
  setStyleTokens: (v: StyleTokens | null) => void;
  followStyle: boolean;
  /** 复位成跟随画风(按当前画风重推;自由发挥 → 回到「不指定」)。 */
  resetFollowStyle: () => void;
  fontStyle: string;
  setFontStyle: (v: string) => void;
}

/** 色卡上露 4 槽:与长图预设卡同一批槽位,换个地方看到的是同一张脸。 */
const SWATCH_SLOTS = ["accent", "secondary", "ink", "bg"] as const;

/**
 * ★宽度写死 96px 与上面那排画风缩略图对齐(2026-08-26 由 84px 一起加宽),**不用 `grid-cols-N`**:
 * 这个面板在单帧创建页是**满宽**的(不是长图那种 380px 侧栏),
 * 四列网格会把每张色卡拉到 300px 宽、中间飘着 4 个 12px 小色块 —— 实测截图里就是这样。
 */
const TILE =
  // 2026-08-26 与画风排同批:配色卡名(高端美妆/3C 科技/食品促销…)原为 11px 且带 truncate,
  // 84px 卡宽下 4 字刚好卡边。加宽到 96px + 字号 14px,与上面那排画风缩略图**继续对齐**
  // (对齐关系见本常量下方那条头注;两处宽度必须一起改,单改一处就错位)。
  "flex w-24 shrink-0 flex-col items-center gap-1 rounded-lg border px-1 py-1.5 text-sm transition-colors " +
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary";
const TILE_ON = "border-primary bg-primary/10 font-medium text-primary";
const TILE_OFF = "border-border/60 text-muted-foreground hover:border-primary/50";

export function StyleColorRow({
  styleTokens,
  setStyleTokens,
  followStyle,
  resetFollowStyle,
  fontStyle,
  setFontStyle,
}: Props) {
  const activePresetKey = followStyle ? undefined : matchStyleTokenPreset(styleTokens)?.key;

  // 三种态三句实话 —— ★跟随但推不出配色时不许说「跟着画风走」(自由发挥没有画风可跟)
  const hint = !followStyle
    ? "配色已单独设定,换画风不再覆盖它"
    : styleTokens
      ? "配色正跟着画风走,换画风会一起换"
      : "配色交给 AI 自己定;选个画风、或点一张色卡定下来";

  return (
    <div className="space-y-2 border-t border-border/50 pt-3" data-poster-color-row>
      {/* 命名纪律 3.2.4:这一层对用户只叫「配色」。
          ★不再另挂一个「跟随画风 / 已选定」徽标:选中的那张卡本身就是状态,
          下面还有一句话解释 —— 同一件事说三遍正是本波要治的病。 */}
      <p className="text-sm font-medium text-muted-foreground">配色</p>

      <div role="radiogroup" aria-label="配色" className="flex gap-2 overflow-x-auto pb-1">
        {/* ① 跟随画风(常驻首位)—— 有配色就把当前色板露出来,让用户看见"跟随"跟出了什么 */}
        <button
          type="button"
          role="radio"
          aria-checked={followStyle}
          onClick={resetFollowStyle}
          title="配色由画风决定;没选画风时交给 AI 自己定"
          data-color-follow
          className={cn(TILE, followStyle ? TILE_ON : TILE_OFF)}
        >
          <span className="flex gap-0.5">
            {styleTokens ? (
              SWATCH_SLOTS.map((k) => (
                <span
                  key={k}
                  className="h-3 w-3 rounded-sm border border-border/40"
                  style={{ backgroundColor: styleTokens.palette[k] }}
                />
              ))
            ) : (
              <span className="grid h-3 w-[54px] place-items-center rounded-sm border border-dashed border-current/50 text-2xs">
                AI
              </span>
            )}
          </span>
          跟随画风
        </button>

        {/* ② 6 张预设色卡(与长图同一批,产物颜色硬编码 —— §3.2.5 白名单) */}
        {STYLE_TOKEN_PRESETS.map((p) => (
          <button
            key={p.key}
            type="button"
            role="radio"
            aria-checked={activePresetKey === p.key}
            title={p.hint}
            onClick={() => setStyleTokens(copyStyleTokens(p.tokens))}
            data-color-preset={p.key}
            className={cn(TILE, activePresetKey === p.key ? TILE_ON : TILE_OFF)}
          >
            <span className="flex gap-0.5">
              {SWATCH_SLOTS.map((k) => (
                <span
                  key={k}
                  className="h-3 w-3 rounded-sm border border-border/40"
                  style={{ backgroundColor: p.tokens.palette[k] }}
                />
              ))}
            </span>
            <span className="w-full truncate text-center">{p.label}</span>
          </button>
        ))}
      </div>

      <p className="text-2xs text-muted-foreground" data-color-hint>
        {hint}
      </p>

      {/* ③ 字体档并入配色(定案 3.2.1)。单帧这条路它走【字体风格】提示词注入 ——
          与配色是两个独立选择,换画风不冲字体。 */}
      <div className="space-y-1.5 pt-1">
        <p className="text-sm font-medium text-muted-foreground">字体</p>
        <div className="flex flex-wrap gap-1.5" role="group" aria-label="字体">
          {FONT_STYLES.map((f) => {
            const active = fontStyle === f.key;
            return (
              <button
                key={f.key}
                type="button"
                onClick={() => setFontStyle(f.key)}
                aria-pressed={active}
                title={f.phrase ?? "由内容与画风自动决定字体气质"}
                className={cn(
                  "rounded-full border px-3 py-1 text-[12.5px] transition-colors",
                  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary",
                  active
                    ? "border-primary bg-primary/10 font-medium text-primary"
                    : "border-border/60 text-muted-foreground hover:border-primary/50"
                )}
              >
                {/* 2026-07-30 —— 加字形示例。
                    ★字体是这三项(画风/配色/字体)里**唯一没有视觉样例**的:画风有缩略图、
                      配色本身就是色块,而字体只有一个中文标签。「衬线」两个字对没设计背景的
                      用户没有信息量 —— 一个按该档字形渲染的「永」比标签管用。
                    ★用系统字族近似(我们不为此加字体包):圆润→圆体近似、手写→草书族、
                      衬线→宋体/Georgia、现代无衬线→Helvetica。auto 档没有具体字形,不画。 */}
                {f.key !== FONT_STYLE_AUTO && (
                  <span
                    aria-hidden
                    className="mr-1.5 text-[15px] leading-none"
                    style={{ fontFamily: FONT_PREVIEW_FAMILY[f.key] }}
                  >
                    永
                  </span>
                )}
                {f.label}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
